import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchData } from '@/services/api/data'
import {
  fetchWork,
  updateWork,
  reassignComposer,
  type UpdateWorkInput,
  type ReassignComposerInput,
} from '@/services/api/works'
import type { Work } from '@brent/shared/types'
import { dataKeys } from './useDashboardData'
import { composerKeys } from './useComposers'

export const workKeys = {
  all: ['works'] as const,
  lists: () => [...workKeys.all, 'list'] as const,
  list: () => [...workKeys.lists()] as const,
  details: () => [...workKeys.all, 'detail'] as const,
  detail: (id: number) => [...workKeys.details(), id] as const,
}

export function useWorks() {
  return useQuery<Work[]>({
    queryKey: workKeys.list(),
    queryFn: async () => {
      const data = await fetchData()
      return data.works
    },
  })
}

export function useWork(id: number) {
  return useQuery({
    queryKey: workKeys.detail(id),
    queryFn: () => fetchWork(id),
    enabled: id > 0,
  })
}

export function useUpdateWork() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateWorkInput }) => updateWork(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: workKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}

export function useReassignComposer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ workId, input }: { workId: number; input: ReassignComposerInput }) =>
      reassignComposer(workId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workKeys.detail(variables.workId) })
      queryClient.invalidateQueries({ queryKey: workKeys.lists() })
      queryClient.invalidateQueries({ queryKey: composerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}
